/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  activeTab: 'shipments', // 'shipments' | 'reconcile'
  activeCategory: 'awaitingShipment', // 'awaitingShipment' | 'shipped'
  activeSubcategory: 'all', // 'all' | pharmacyId (awaiting) | 'today' | 'yesterday' | 'recent' | 'voided' (shipped)
  activeFacility: 'all', // facility tab in content area (pharmacyId or 'all')
  activePreset: 'all', // 'all' | 'current' | 'badAddresses' | 'manual'
  counts: {
    loading: false,
    awaiting: { total: 0, byPharmacy: {} },
    shipped: {
      total: 0,
      all: 0,
      today: 0,
      yesterday: 0,
      recent: 0,
      voided: 0,
      byPharmacy: {},
      todayByPharmacy: {},
      yesterdayByPharmacy: {},
      recentByPharmacy: {},
      voidedByPharmacy: {},
    },
  },
  presetCounts: { loading: false, all: 0, current: 0, badAddresses: 0, manual: 0 },
  list: {
    currentPage: 1,
    pages: {},
    count: 0,
    loading: false,
    filter: { flatSearch: undefined },
    sortBy: { sortKey: 'createdAt', sortOrder: 'ASC' },
  },
};

const shipmentModuleSlice = createSlice({
  name: 'shipmentModule',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setActiveCategory: (state, action) => {
      state.activeCategory = action.payload;
      state.activeSubcategory = 'all';
      state.activeFacility = 'all';
      state.activePreset = 'all';
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    setActiveSubcategory: (state, action) => {
      state.activeSubcategory = action.payload;
      state.activeFacility = 'all';
      state.activePreset = 'all';
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    /** Sets category to awaitingShipment and subcategory to pharmacyId in one update to avoid intermediate state (e.g. subcategory='all') that can trigger an extra fetch without fulfillingPharmacyId. */
    setAwaitingFacility: (state, action) => {
      state.activeCategory = 'awaitingShipment';
      state.activeSubcategory = action.payload;
      state.activeFacility = 'all';
      state.activePreset = 'all';
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    /** Sets category to shipped and subcategory (all | recent | voided) in one update to avoid intermediate state that triggers extra fetch with wrong filter (e.g. isVoided: false when navigating to voided). */
    setShippedSubcategory: (state, action) => {
      state.activeCategory = 'shipped';
      state.activeSubcategory = action.payload;
      state.activeFacility = 'all';
      state.activePreset = 'all';
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    setActiveFacility: (state, action) => {
      state.activeFacility = action.payload;
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    setActivePreset: (state, action) => {
      state.activePreset = action.payload;
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    setCountsLoading: (state, action) => {
      state.counts.loading = action.payload;
    },
    setCounts: (state, action) => {
      state.counts = { ...action.payload, loading: false };
    },
    setPresetCountsLoading: (state, action) => {
      state.presetCounts.loading = action.payload;
    },
    setPresetCounts: (state, action) => {
      state.presetCounts = { ...action.payload, loading: false };
    },
    setListLoading: (state, action) => {
      state.list.loading = action.payload;
    },
    loadListPage: (state, action) => {
      const { page, items, count } = action.payload;
      state.list.pages[page] = items;
      state.list.count = count;
      state.list.loading = false;
    },
    setCurrentPage: (state, action) => {
      state.list.currentPage = action.payload;
    },
    updateFilter: (state, action) => {
      state.list.filter = { ...state.list.filter, ...action.payload };
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    resetFilter: (state) => {
      state.list.filter = { flatSearch: undefined };
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    updateSortBy: (state, action) => {
      state.list.sortBy = action.payload;
      state.list.currentPage = 1;
      state.list.pages = {};
    },
    resetListState: (state) => {
      state.list.currentPage = 1;
      state.list.pages = {};
      state.list.count = 0;
      state.list.loading = false;
    },
    updateShipmentListItemTags: (state, action) => {
      const { shipmentId, tags } = action.payload;
      Object.values(state.list.pages).forEach((pageItems) => {
        const item = pageItems.find((s) => s.shipmentId === shipmentId);
        if (item) {
          item.tags = tags;
        }
      });
    },
  },
});

export const shipmentModuleActions = shipmentModuleSlice.actions;
export default shipmentModuleSlice.reducer;
